import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Clock, BarChart3, Bell, History } from 'lucide-react';
import { MobileSheet } from '@/shared/components/MobileSheet';
import { useJukeboxCliente } from '../contexts/JukeboxClienteContext';
import { useJukeboxAuth } from '../hooks/useJukeboxAuth';
import { formatPhoneBrMask, isValidPhoneBr, stripPhoneDigits, toPhoneE164Br } from '@/shared/lib/phoneUtils';

const BENEFITS = [
  { icon: History, text: 'Histórico de músicas salvo para sempre' },
  { icon: BarChart3, text: 'Limite ampliado: 5 pedidos ativos' },
  { icon: Bell, text: 'Avisos no WhatsApp automáticos' },
  { icon: Clock, text: 'Estatísticas pessoais' },
] as const;

export default function JukeboxLoginSheet() {
  const { isLoginOpen, closeLogin, sessionId, onLoginSuccess } = useJukeboxCliente();
  const telaId = useTelaIdFromUrl();

  const { step, loading, error, sendOtp, verifyOtp, reset } = useJukeboxAuth({
    telaId,
    sessionId,
    onSuccess: onLoginSuccess,
  });

  const [phone, setPhone] = useState('');
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!isLoginOpen) {
      reset();
      setPhone('');
      setOtpDigits(['', '', '', '', '', '']);
    }
  }, [isLoginOpen, reset]);

  useEffect(() => {
    if (step === 'code') {
      inputRefs.current[0]?.focus();
    }
  }, [step]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = stripPhoneDigits(e.target.value);
    setPhone(raw);
  };

  const handleSend = async () => {
    if (!isValidPhoneBr(phone)) return;
    await sendOtp(toPhoneE164Br(phone));
  };

  const handleOtpInput = useCallback((index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newDigits.every(d => d !== '')) {
      const code = newDigits.join('');
      verifyOtp(toPhoneE164Br(phone), code);
    }
  }, [otpDigits, phone, verifyOtp]);

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      const digits = pasted.split('');
      setOtpDigits(digits);
      verifyOtp(toPhoneE164Br(phone), pasted);
    }
  };

  return (
    <MobileSheet aberto={isLoginOpen} titulo="Entrar no Jukebox" onClose={closeLogin}>
      <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pt-2 pb-6">
        <button
          onClick={closeLogin}
          aria-label="Fechar login"
          className="absolute top-2 right-4 p-2 text-white/55 hover:text-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 rounded-lg transition-colors"
        >
          <X size={20} />
        </button>

        {step === 'phone' && (
          <div className="space-y-5">
            <div className="text-center">
              <h2 className="text-lg font-bold text-white">Entrar no Jukebox</h2>
              <p className="text-sm text-white/50 mt-1">
                Receba um código no WhatsApp para entrar
              </p>
            </div>

            <div className="space-y-3">
              {BENEFITS.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0">
                    <Icon size={16} className="text-orange-400" />
                  </div>
                  <span className="text-sm text-white/70">{text}</span>
                </div>
              ))}
            </div>

            <div className="space-y-3 pt-2">
              <div className="relative">
                <input
                  type="tel"
                  inputMode="numeric"
                  aria-label="Número de WhatsApp"
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? 'jukebox-login-error' : undefined}
                  placeholder="(62) 99999-9999"
                  value={formatPhoneBrMask(phone)}
                  onChange={handlePhoneChange}
                  className="w-full px-4 py-3.5 rounded-xl bg-white/[0.06] border border-white/[0.08] text-white placeholder:text-white/30 text-base focus:outline-none focus:border-orange-500/50 transition-colors"
                />
              </div>

              {error && (
                <p id="jukebox-login-error" role="alert" className="text-sm text-red-400 text-center">{error}</p>
              )}

              <button
                onClick={handleSend}
                disabled={!isValidPhoneBr(phone) || loading}
                className="w-full py-3.5 rounded-xl font-semibold text-white bg-gradient-to-r from-orange-500 to-rose-500 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60 transition-opacity"
              >
                {loading ? 'Enviando...' : 'Enviar código'}
              </button>

              <button
                onClick={closeLogin}
                className="w-full py-3 text-sm text-white/55 hover:text-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-lg transition-colors"
              >
                Continuar sem login
              </button>
            </div>
          </div>
        )}

        {step === 'code' && (
          <div className="space-y-5">
            <div className="text-center">
              <h2 className="text-lg font-bold text-white">Digite o código</h2>
              <p className="text-sm text-white/50 mt-1">
                Enviamos um código de 6 dígitos para o WhatsApp
              </p>
            </div>

            <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
              {otpDigits.map((digit, i) => (
                <input
                  key={i}
                  ref={el => { inputRefs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  aria-label={`Dígito ${i + 1} do código de acesso`}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? 'jukebox-login-error' : undefined}
                  maxLength={1}
                  value={digit}
                  onChange={e => handleOtpInput(i, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(i, e)}
                  className="w-11 h-[52px] text-center text-xl font-bold rounded-xl bg-white/[0.06] border border-white/[0.08] text-white focus:outline-none focus:border-orange-500/50 focus-visible:ring-2 focus-visible:ring-orange-400/60 transition-colors"
                />
              ))}
            </div>

            {error && (
              <p id="jukebox-login-error" role="alert" className="text-sm text-red-400 text-center">{error}</p>
            )}

            {loading && (
              <p className="text-sm text-white/50 text-center">Verificando...</p>
            )}

            <button
              onClick={() => { reset(); setOtpDigits(['', '', '', '', '', '']); }}
              className="w-full py-3 text-sm text-white/55 hover:text-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-lg transition-colors"
            >
              Voltar
            </button>
          </div>
        )}

        {step === 'success' && (
          <div className="space-y-4 text-center py-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-white">Pronto!</h2>
            <p className="text-sm text-white/50 px-4">
              Você está logado. Avisos no WhatsApp estão ativos — desative quando quiser no Perfil.
            </p>
          </div>
        )}
      </div>
    </MobileSheet>
  );
}

function useTelaIdFromUrl(): string {
  const match = window.location.pathname.match(/\/jukebox\/([^/]+)/);
  return match?.[1] ?? '';
}
